/* @flow */

import { DateTime } from 'luxon';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  isBraindateCancelled,
  isBraindateDeclined,
  getBraindateTimeSlot,
  getBraindateConversation,
  getBraindateId,
} from '@e180/domain/lib/braindate/util';
import { getConversationLastUpdateTimestamp } from '@e180/domain/lib/conversation/util';

import styles from './BraindateCardLastUpdate.style';

import braindateMessages from 'src/shared/app/braindate/l10n/braindateL10n';
import Time from 'src/shared/app/base/date/component/Time';
import dateMessages from 'src/shared/app/base/date/l10n/dateL10n';

import type { Braindate } from '@e180/domain/lib/braindate/type';

type Props = {
  braindate: Braindate,
};

const useStyles = createUseStyles(styles);

const BraindateCardLastUpdate = ({ braindate }: Props): React.Node => {
  const id = getBraindateId(braindate);
  const conversation = getBraindateConversation(braindate);
  const time = getConversationLastUpdateTimestamp(conversation);
  const date = getBraindateTimeSlot(braindate);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  if (
    date ||
    isBraindateCancelled(braindate) ||
    isBraindateDeclined(braindate)
  ) {
    return null;
  }

  const today = DateTime.local().startOf('day');
  const yesterday = today.minus({ day: 1 });
  const dateTime = DateTime.fromISO(time);
  const ns = 'braindate-last-update';
  const labelId = `${ns}-label-${id}`;
  const timeId = `${ns}-label-${id}`;

  let txt;

  if (dateTime.hasSame(today, 'day')) {
    txt = <Time id={timeId} date={time} />;
  } else if (dateTime.hasSame(yesterday, 'day')) {
    txt = intl.formatMessage(dateMessages.yesterday);
  }

  if (txt) {
    return (
      <span aria-labelledby={`${labelId} ${timeId}`}>
        <span className={classes.label} id={labelId}>
          {intl.formatMessage(braindateMessages.braindateLastUpdate)}
        </span>
        {txt}
      </span>
    );
  }

  return null;
};

export default compose(React.memo)(BraindateCardLastUpdate);
